<template>
  <div>
    <template v-if="isLoading">
      <UtilPlaceholderUac class="mb-4" />

      <UtilPlaceholder class="h-6 mb-2" />
      <UtilPlaceholder class="w-6/12 h-6 mb-2" />
      <UtilPlaceholder class="w-4/12 h-4 mb-4" />

      <UtilPlaceholder class="h-64 mb-4" />

      <div class="flex">
        <UtilPlaceholder class="w-6/12 h-12 mr-2" />
        <UtilPlaceholder class="w-6/12 h-12" />
      </div>
    </template>

    <template v-else>
      <h1 class="mb-4 text-xl font-medium">
        <span class="text-brand" :class="{ 'text-danger': listing.type === listingType.WANTED }">
          <template v-if="listing.type === listingType.AUCTION">Auction</template>
          <template v-else>
            {{ listing.type === listingType.WANTED ? $t('listing.wanted') : $t('listing.forSale') }}
          </template>
        </span>

        {{ title }}

        <UtilLocationSummary
          :location="listing.location[0]"
          :showLocality="listingIs.detailsVisible"
          flagSize="sm"
          targetClass="mb-1 text-base font-normal"
        />
      </h1>

      <div class="flex flex-wrap items-center mb-2">
        <span v-if="compAge" class="mb-2 mr-5 text-gray-darker">
          <FontAwesomeIcon :icon="faCalendarAlt" fixedWidth class="mr-1" />
          {{ compAge }}
        </span>

        <span class="mb-2 mr-5 text-gray-darker">
          <FontAwesomeIcon :icon="faClock" fixedWidth class="mr-1" />

          <span v-if="listingIs.event && event">
            {{ `Live Auction ${$date(event.startDate, $date.legacyFormat).format('ddd, Do MMM HH:mm')}` }}
          </span>

          <span v-else>
            {{ $t('listing.listedAgo', { dateAgo: $date(listing.start, $date.legacyFormat).fromNow() }) }}
          </span>
        </span>

        <span class="mb-2 text-gray-darker">
          <FontAwesomeIcon :icon="faEye" fixedWidth class="mr-1" />
          {{ $t('listing.viewingCount', { count: $n($store.getters['listing/viewCount'](listing, listingIs)) }) }}
        </span>
      </div>

      <div v-if="compMetaTags.length || listing.meta.farmAssured" class="flex flex-wrap mb-6">
        <span v-if="listing.meta.farmAssured" class="relative block w-6/12 md:w-full lg:w-6/12">
          <FontAwesomeIcon :icon="faCheckCircle" fixedWidth class="mr-1 text-brand" />
          Farm Assured
          <span
            v-if="!listingIs.redTractorVerified && listingIs.inEngland"
            class="block mb-1 ml-8 text-sm leading-tight uppercase text-gray-lighter"
          >
            Unverified
          </span>
        </span>

        <span v-for="tag in compMetaTags" :key="tag" class="block w-6/12 md:w-full lg:w-6/12">
          <FontAwesomeIcon :icon="faCheckCircle" fixedWidth class="mr-1 text-brand" />
          {{ tag }}
          <span
            v-if="listing.meta.organicAssured && tag === 'Organic'"
            class="block mb-1 ml-8 text-sm leading-tight uppercase text-gray-lighter"
          >
            Unverified
          </span>
        </span>
      </div>

      <UtilListingRedTractorAssured
        v-if="appIs.auth && listingIs.redTractorVerified"
        :listing="listing"
        :listingIs="listingIs"
      />

      <div v-if="listingIs.wanted && listing.movement.radius <= 200" class="mb-6">
        Wanted within {{ listing.movement.radius }} miles of {{ listing.location[0].region }}
      </div>

      <!-- Allowed to view price? -->
      <template v-if="appIs.auth && !listingIs.timeRestricted">
        <div class="flex items-center justify-center px-2 py-1 text-xl font-medium bg-gray-lightest">
          <UtilPrice :price="listing.price" class="flex-shrink-0" />
        </div>

        <!-- <UtilTextLink
          v-if="appIs.auth && listingIs.forSale && !listingIs.event"
          @click="openFinanceApplication()"
          class="block w-full mt-1 text-sm text-center no-underline text-gray-darker"
        >
          Finance available from
          <span class="underline">£5.50 per month</span>
        </UtilTextLink> -->
      </template>

      <template v-if="listingIs.dead">
        <UtilMessage v-if="listingIs.sold" type="information" class="mt-4">This listing has sold</UtilMessage>

        <UtilMessage v-else type="information" class="mt-4">This listing has ended</UtilMessage>
      </template>

      <div
        v-else-if="appIs.auth && !listingIs.timeRestricted"
        class="flex flex-no-wrap mt-4 listing-actions md:flex-wrap lg:flex-no-wrap align-center"
      >
        <!-- LISTING OWNER ON SMALL SCREEN -->
        <template v-if="listingIs.owner && listingIs.forSale && $breakpoint.smAndDown && !listingIs.auction">
          <UtilButton
            :to="`/edit/listing/${listing.listingRef}`"
            class="w-6/12 mr-2 bg-white"
            :class="{ 'w-full': !listingIs.live }"
          >
            <FontAwesomeIcon :icon="faPencil" />
            <span>{{ $t('buttons.editListing') }}</span>
          </UtilButton>

          <UtilButton
            v-if="listingIs.live"
            @click="$store.dispatch('listing/openListingSold', listing)"
            color="primary"
            class="w-6/12"
          >
            <FontAwesomeIcon :icon="faGavel" />
            <span>{{ $t('buttons.markAsSold') }}</span>
          </UtilButton>
        </template>

        <!-- BUYER OR LARGER SCREEN -->
        <template v-else>
          <!-- LIVE AUCTION -->
          <template v-if="listingIs.event">
            <UtilButton
              :to="`/events/${listing.eventRef}`"
              color="primary"
              class="w-6/12 mb-2 mr-2 lg:mb-0 md:w-full lg:w-6/12 md:mr-0 lg:mr-2"
            >
              <FontAwesomeIcon :icon="faGavel" />
              <span>View Event</span>
            </UtilButton>

            <UtilButton
              :to="`/events/${listing.eventRef}?redirect=true`"
              color="secondary"
              class="w-6/12 mb-2 lg:mb-0 md:w-full lg:w-6/12"
            >
              <span>View Bidding</span>
            </UtilButton>
          </template>

          <!-- BUY NOW -->
          <template v-else-if="listingIs.buyNow">
            <UtilMessage v-if="listingIs.buyNowPendingSale" type="information" class="w-full">
              This listing is currently reserved
            </UtilMessage>

            <UtilButton v-else @click="buyNow()" color="primary" block>
              <FontAwesomeIcon :icon="faCreditCard" />
              <span>Buy Now</span>
            </UtilButton>
          </template>

          <!-- CLASSIFIED -->
          <template v-else-if="!listingIs.auction">
            <UtilButton
              @click="contactSeller()"
              class="mb-2 bg-white lg:mb-0"
              :class="{
                'w-6/12 md:w-full lg:w-6/12 mr-2 md:mr-0 lg:mr-2': listingIs.forSale,
                'w-full': listingIs.wanted
              }"
              :color="listingIs.forSale ? 'outline' : 'primary'"
            >
              <FontAwesomeIcon :icon="faCommentAltLines" />

              <span>
                {{
                  listingIs.inPortal && listingIs.portalAuctionPartner
                    ? $t('buttons.contactAgent')
                    : $t('buttons.contactSeller')
                }}
              </span>
            </UtilButton>

            <UtilButton
              v-if="listingIs.forSale"
              @click="buyingOptions()"
              color="primary"
              class="w-6/12 mb-2 lg:mb-0 md:w-full lg:w-6/12"
            >
              <FontAwesomeIcon :icon="faMoneyCheckEdit" />
              <span>{{ listingIs.owner ? $t('buttons.sellingOptions') : $t('buttons.buyingOptions') }}</span>
            </UtilButton>
          </template>

          <template v-if="listingIs.auction">
            <UtilButton
              @click="contactSeller()"
              class="mb-2 bg-white lg:mb-0"
              :class="{
                'w-6/12 md:w-full lg:w-6/12 mr-2 md:mr-0 lg:mr-2': listingIs.forSale,
                'w-full': listingIs.wanted
              }"
              :color="listingIs.forSale ? 'outline' : 'primary'"
            >
              <FontAwesomeIcon :icon="faCommentAltLines" />

              <span>
                {{
                  listingIs.inPortal && listingIs.portalAuctionPartner
                    ? $t('buttons.contactAgent')
                    : $t('buttons.contactSeller')
                }}
              </span>
            </UtilButton>

            <UtilButton
              @click.native.stop="$openWindow(`https://new.sellmylivestock.com/listings/${listing.listingRef}`)"
              color="primary"
              class="w-full"
            >
              <FontAwesomeIcon :icon="faGavel" />
              <span>View Auction</span>
            </UtilButton>
          </template>
        </template>
      </div>

      <UtilMessage
        v-if="!appIs.auth && !listingIs.dead"
        @click.native="restrictedAction()"
        large
        class="mt-2 cursor-pointer"
      >
        <span class="font-semibold">Login or Register</span>

        <UtilDivider class="my-4" />

        <p class="my-2">
          You must login to see the full details of this listing and the ability to buy or contact the owner.
        </p>

        <UtilButton color="primary" class="w-full font-medium">Login or Register</UtilButton>
      </UtilMessage>

      <UtilMessage
        v-else-if="appIs.auth && listingIs.timeRestricted && !listingIs.sold"
        :showIcon="false"
        @click.native="restrictedAction()"
        class="flex-col cursor-pointer"
      >
        <div class="flex justify-center align-middle">
          <FontAwesomeIcon :icon="faInfoCircle" class="mt-1 mr-2 icon" fixed-width />
          <h2 class="inline-block">
            {{ app.activeSubscriptionPlanName }} members wait
            {{ app.featureRestrictions.limits.viewListingDelay }} hours for full details
          </h2>
        </div>

        <p class="mt-2">
          You can unlock full details of this listing and contact the lister within
          {{ app.featureRestrictions.limits.viewListingDelay }} hours. Don't want to wait? Upgrade to Gold
          membership now for immediate access on all listings at all times.
        </p>

        <UtilButton color="primary" class="block mx-auto mt-4 font-medium">
          Upgrade your membership plan
        </UtilButton>
      </UtilMessage>

      <UtilMessage v-else-if="listingIs.buyNow && !listingIs.buyNowPendingSale" type="information" class="mt-4">
        This listing is available for you to Buy Now with just a couple of easy clicks.
        <UtilTextLink @click="buyNow()">Find out more</UtilTextLink>
      </UtilMessage>
    </template>
  </div>
</template>

<script>
import {
  faCalendarAlt,
  faCheckCircle,
  faChevronRight,
  faClock,
  faCommentAltLines,
  faCreditCard,
  faEye,
  faGavel,
  faMoneyCheckEdit,
  faPencil,
  faTruckContainer
} from '@fortawesome/pro-regular-svg-icons'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { listingType } from '@/lib/mapping'
import getLivestockAge from '@/lib/helpers/livestock-age'
import UtilButton from '@/components/utils/UtilButton'
import UtilPrice from '@/components/utils/UtilPrice'
import UtilTextLink from '@/components/utils/UtilTextLink'
import UtilMessage from '@/components/utils/UtilMessage'
import UtilPlaceholder from '@/components/utils/placeholders/UtilPlaceholder'
import UtilPlaceholderUac from '@/components/utils/placeholders/UtilPlaceholderUac'
import UtilLocationSummary from '@/components/utils/UtilLocationSummary'
import UtilListingRedTractorAssured from '@/components/utils/listing/UtilListingRedTractorAssured'
import UtilDivider from '@/components/utils/UtilDivider'
import LegacyApi from '@/lib/api/legacy'

export default {
  name: 'ListingOverview',
  components: {
    UtilButton,
    UtilPrice,
    UtilTextLink,
    UtilMessage,
    UtilPlaceholder,
    UtilPlaceholderUac,
    UtilLocationSummary,
    UtilListingRedTractorAssured,
    UtilDivider
  },
  props: {
    isLoading: Boolean,
    listing: {
      type: Object,
      required: true
    },
    listingIs: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    event: Object
  },
  data() {
    return {
      listingType,
      eventStartDate: null,
      eventApi: new LegacyApi(),
      faCalendarAlt,
      faEye,
      faCommentAltLines,
      faMoneyCheckEdit,
      faCreditCard,
      faCheckCircle,
      faGavel,
      faPencil,
      faClock,
      faTruckContainer,
      faChevronRight,
      faInfoCircle
    }
  },
  computed: {
    compMetaTags() {
      return this.$store.getters['listing/overviewMetaTags'](this.listing)
    },

    compUser() {
      return this.listingIs.timeRestricted || !this.appIs.auth ? {} : this.listing.user[0]
    },

    compCompany() {
      return this.listingIs.timeRestricted || !this.appIs.auth ? {} : this.listing.company[0]
    },

    compAge() {
      if (!this.listingIs.livestock) {
        return ''
      }

      return getLivestockAge(this.listing)
    }
  },

  methods: {
    restrictedAction() {
      if (!this.appIs.auth) {
        this.login()
      } else if (this.listingIs.timeRestricted) {
        this.$store.dispatch('payments/showSubscriptionPlanChooser', {
          origin: 'listing'
        })
      }
    },

    contactSeller() {
      // Handle user using dev tools to remove the blur
      if (this.listingIs.timeRestricted || !this.appIs.auth) {
        return false
      }

      const modalName = this.listingIs.forSale ? 'ModalListingContactSeller' : 'ModalListingContactWanted'

      this.$modal.open(modalName, {
        listing: this.listing,
        listingIs: this.listingIs,
        title: this.title
      })
    },

    buyingOptions() {
      // Handle user using dev tools to remove the blur
      if (this.listingIs.timeRestricted || !this.appIs.auth) {
        return false
      }

      this.$analytics.addEvent({
        category: 'listing',
        action: 'Buying options clicked',
        label: this.listing.listingRef
      })

      this.$modal.open('ModalListingBuyingOptions', {
        listing: this.listing,
        listingIs: this.listingIs,
        otherParty: this.listingIs.detailsVisible ? this.listing.user[0] : null,
        isBuyerAndSellerAlreadyCommunicating: false
      })
    },

    buyNow() {
      if (!this.appIs.auth) {
        return false
      }

      this.$modal.open('ModalListingBuyNow', {
        listing: this.listing,
        listingIs: this.listingIs,
        otherParty: this.listing.user[0]
      })
    },

    login() {
      this.$modal.open('ModalAuthLogin', { redirectToDashboard: false })
    },

    openFinanceApplication() {
      this.$store.dispatch('modal/showModal', {
        modalName: 'ModalFinanceApplication',
        modalData: { listing: this.listing }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.disabled {
  position: relative;
}

.disabled::v-deep > * {
  filter: blur(2px);
}

.disabled:after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1;
  background: rgba(255, 255, 255, 0.3);
}

.disabled:before {
  @apply px-4 py-1 font-medium rounded;
  @apply bg-blue-100;
  content: attr(data-disabled-label);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  white-space: nowrap;
  box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

#app.xs .listing-actions {
  @apply justify-center shadow fixed bg-gray-darker px-2 py-2 border-t border-gray;
  bottom: $bottom-nav-height;
  left: 0;
  right: 0;
  z-index: 10;

  & button,
  & a {
    margin-bottom: 0px;
  }
}

#app.ios .listing-actions {
  bottom: calc(#{$bottom-nav-height} + constant(safe-area-inset-bottom));
  bottom: calc(#{$bottom-nav-height} + env(safe-area-inset-bottom));
}
</style>
